import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { X, Plus, ChevronDown, Upload } from "lucide-react";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024; // 3 MB

/**
 * Load the reCAPTCHA v3 script once. Returns a promise resolving when window.grecaptcha is ready.
 */
function loadRecaptcha() {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve(null);
  if (window.__wofyRecaptchaPromise) return window.__wofyRecaptchaPromise;
  window.__wofyRecaptchaPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.onload = () => window.grecaptcha.ready(() => resolve(window.grecaptcha));
    s.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(s);
  });
  return window.__wofyRecaptchaPromise;
}

/**
 * Unified public-submission form.
 * type: "volunteer_opportunity" | "organization" | "housing_project" | "help_offer"
 */
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function emptyHours() {
  return DAYS.reduce((acc, d) => {
    acc[d] = { open: "", close: "", break_start: "", break_end: "", closed: false };
    return acc;
  }, {});
}

const TYPE_META = {
  volunteer_opportunity: {
    title: "List a Volunteer Program or Opportunity",
    intro: "Share a volunteering opportunity so kind hearts can find and join it. Your submission goes to moderation before publishing.",
    testid_prefix: "vol-submit",
  },
  organization: {
    title: "Submit Your Organization",
    intro: "Add your nonprofit, charity, community group or cooperative. We verify every organization before listing.",
    testid_prefix: "org-submit",
  },
  housing_project: {
    title: "Suggest / Submit a Housing Project",
    intro: "Propose a housing project — a family in need, a rebuild, a shelter, or a community housing initiative.",
    testid_prefix: "housing-submit",
  },
  help_offer: {
    title: "Offer Your Help",
    intro: "Tell the world what you can offer — time, skills, goods, transport, mentoring, funds. We'll match you with a cause.",
    testid_prefix: "help-offer-submit",
  },
  change_maker: {
    title: "Suggest a Change Maker",
    intro: "Nominate a person whose life and work has moved you. Every hero is verified before being featured on the Change Makers page.",
    testid_prefix: "cm-submit",
  },
};

export default function PublicSubmissionForm({ type, onSubmitted }) {
  const { user } = useAuth();
  const meta = TYPE_META[type];
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  // shared submitter
  const [submitter, setSubmitter] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });

  // type-specific data — one big blob
  const [data, setData] = useState({});
  const [categoryOther, setCategoryOther] = useState("");
  const [areasServed, setAreasServed] = useState([]);
  const [needVolunteers, setNeedVolunteers] = useState(false);
  const [hours, setHours] = useState(emptyHours());
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    http.get("/public-submissions/config").then((r) => setCfg(r.data)).catch(() => {});
    // Preload reCAPTCHA (non-blocking)
    loadRecaptcha().catch(() => {});
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onImagePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error(`Image must be under ${Math.round(MAX_IMAGE_SIZE_BYTES / 1024 / 1024)}MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("image_url", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please accept the submission terms.");
      return;
    }
    // Build the outgoing payload per type
    const payload = { type, submitter, data: { ...data }, consent };

    if (type === "volunteer_opportunity") {
      if (data.category === "Other" && categoryOther.trim()) {
        payload.data.category = `Other: ${categoryOther.trim()}`;
      }
    }
    if (type === "organization") {
      payload.data.areas_served = areasServed;
      payload.data.need_volunteers = needVolunteers;
      payload.data.working_hours = hours;
    }
    if (type === "housing_project") {
      if (data.housing_type === "Other" && categoryOther.trim()) {
        payload.data.housing_type = `Other: ${categoryOther.trim()}`;
      }
    }
    if (type === "help_offer") {
      if (data.offer_type === "Other" && categoryOther.trim()) {
        payload.data.offer_type = `Other: ${categoryOther.trim()}`;
      }
    }

    setBusy(true);
    try {
      // reCAPTCHA v3 token (invisible). Soft-fail: if it can't load
      // (blocked, misconfigured domain, network), submit anyway — backend flags it.
      if (RECAPTCHA_SITE_KEY) {
        try {
          const gre = await loadRecaptcha();
          if (gre) {
            payload.captcha_token = await gre.execute(RECAPTCHA_SITE_KEY, { action: "public_submission" });
          }
        } catch (err) {
          // Silently proceed — backend will accept but flag as captcha_verified=false.
          // eslint-disable-next-line no-console
          console.warn("reCAPTCHA unavailable; submitting without token", err);
        }
      }

      const { data: resp } = await http.post("/public-submissions", payload);
      toast.success("Thank you — your submission is now under review.");
      setData({});
      setCategoryOther("");
      setAreasServed([]);
      setNeedVolunteers(false);
      setHours(emptyHours());
      setConsent(false);
      onSubmitted?.(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please review the fields.");
    } finally {
      setBusy(false);
    }
  };

  const toggleArea = (a) => {
    setAreasServed((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${meta.testid_prefix}-form`}
      className="wofy-card space-y-5"
    >
      <div>
        <div className="overline">SUBMIT</div>
        <h2 className="font-display text-2xl mt-1">{meta.title}</h2>
        <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{meta.intro}</p>
      </div>

      {/* ===== Submitter (shared) ===== */}
      <fieldset className="grid md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Your name *</span>
          <input required value={submitter.name} onChange={(e) => setSubmitter({ ...submitter, name: e.target.value })} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-submitter-name`} />
        </label>
        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Email *</span>
          <input required type="email" value={submitter.email} onChange={(e) => setSubmitter({ ...submitter, email: e.target.value })} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-submitter-email`} />
        </label>
        <label className="block">
          <span className="text-[11px] font-mono uppercase tracking-wider">Phone</span>
          <input value={submitter.phone} onChange={(e) => setSubmitter({ ...submitter, phone: e.target.value })} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-submitter-phone`} />
        </label>
      </fieldset>

      <div className="wofy-divider" />

      {/* ===== Volunteer opportunity ===== */}
      {type === "volunteer_opportunity" && (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Opportunity title *</span>
            <input required value={data.title || ""} onChange={(e) => set("title", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="e.g. Saturday soup-kitchen shift" data-testid={`${meta.testid_prefix}-title`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Description *</span>
            <textarea required rows={5} value={data.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="What will volunteers do? What impact will it have?" data-testid={`${meta.testid_prefix}-description`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Category *</span>
            <div className="relative mt-1">
              <select required value={data.category || ""} onChange={(e) => set("category", e.target.value)} className="w-full appearance-none rounded-lg border px-3 py-2 text-sm bg-transparent pr-8" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-category`}>
                <option value="" disabled>Select a category</option>
                {cfg?.volunteer_categories?.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
            </div>
          </label>
          {data.category === "Other" && (
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-wider">Specify category *</span>
              <input required value={categoryOther} onChange={(e) => setCategoryOther(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-category-other`} />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">City *</span>
            <input required value={data.city || ""} onChange={(e) => set("city", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-city`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Country *</span>
            <input required value={data.country || ""} onChange={(e) => set("country", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-country`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Hours per week</span>
            <input type="number" min="1" value={data.hours_per_week || ""} onChange={(e) => set("hours_per_week", Number(e.target.value) || null)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Skills needed / good to have</span>
            <input value={data.skills || ""} onChange={(e) => set("skills", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Website / signup link</span>
            <input type="url" value={data.website || ""} onChange={(e) => set("website", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="https://" />
          </label>
        </div>
      )}

      {/* ===== Organization ===== */}
      {type === "organization" && (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Organization name *</span>
            <input required value={data.name || ""} onChange={(e) => set("name", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-name`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Introduction / tagline</span>
            <input value={data.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="Short mission line" />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Detailed description *</span>
            <textarea required rows={5} value={data.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-description`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Street address</span>
            <input value={data.address || ""} onChange={(e) => set("address", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">City *</span>
            <input required value={data.city || ""} onChange={(e) => set("city", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">State / Province</span>
            <input value={data.state || ""} onChange={(e) => set("state", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Zip / Postal code</span>
            <input value={data.zip || ""} onChange={(e) => set("zip", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Country *</span>
            <input required value={data.country || ""} onChange={(e) => set("country", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Website</span>
            <input type="url" value={data.website || ""} onChange={(e) => set("website", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="https://" />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Phone</span>
            <input value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Public email</span>
            <input type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>

          {/* Logo / photo upload */}
          <div className="md:col-span-2">
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Logo / photo (optional, &lt; 3MB)</div>
            <div className="flex items-start gap-4">
              <label className="wofy-btn-ghost cursor-pointer" data-testid={`${meta.testid_prefix}-image-btn`}>
                <Upload className="w-4 h-4" /> Upload image
                <input type="file" accept="image/*" className="hidden" onChange={onImagePicked} />
              </label>
              {data.image_url && (
                <div className="relative">
                  <img src={data.image_url} alt="preview" className="h-24 w-40 object-cover rounded-xl" />
                  <button type="button" onClick={() => set("image_url", "")} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow" aria-label="Remove image">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Working hours */}
          <div className="md:col-span-2">
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Working hours (with break)</div>
            <div className="space-y-1">
              {DAYS.map((d) => (
                <div key={d} className="grid grid-cols-12 gap-2 items-center text-xs">
                  <div className="col-span-2 font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>{d.slice(0, 3)}</div>
                  <label className="col-span-2 inline-flex items-center gap-1 text-[11px]">
                    <input type="checkbox" checked={hours[d].closed} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], closed: e.target.checked } })} />
                    Closed
                  </label>
                  <input type="time" disabled={hours[d].closed} value={hours[d].open} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], open: e.target.value } })} className="col-span-2 rounded border px-2 py-1 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
                  <input type="time" disabled={hours[d].closed} value={hours[d].close} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], close: e.target.value } })} className="col-span-2 rounded border px-2 py-1 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
                  <span className="text-[10px] opacity-60">Break:</span>
                  <input type="time" disabled={hours[d].closed} value={hours[d].break_start} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], break_start: e.target.value } })} className="col-span-1 rounded border px-1 py-1 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
                  <input type="time" disabled={hours[d].closed} value={hours[d].break_end} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], break_end: e.target.value } })} className="col-span-1 rounded border px-1 py-1 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
                </div>
              ))}
            </div>
          </div>

          {/* Areas served — tag multi-select */}
          <div className="md:col-span-2">
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Areas served (pick all that apply)</div>
            <div className="flex flex-wrap gap-2">
              {cfg?.org_areas_served?.map((a) => {
                const active = areasServed.includes(a);
                return (
                  <button
                    type="button"
                    key={a}
                    onClick={() => toggleArea(a)}
                    className="rounded-full px-3 py-1 text-xs border transition-colors"
                    style={active ? { background: "var(--wofy-primary)", color: "#FDFBF7", borderColor: "var(--wofy-primary)" } : { borderColor: "var(--wofy-border)" }}
                    data-testid={`${meta.testid_prefix}-area-${a.replace(/\W+/g, "-").toLowerCase()}`}
                  >
                    {active ? <><X className="w-3 h-3 inline mr-1" />{a}</> : <><Plus className="w-3 h-3 inline mr-1" />{a}</>}
                  </button>
                );
              })}
            </div>
            {areasServed.length > 0 && (
              <div className="mt-2 text-[11px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>{areasServed.length} selected</div>
            )}
          </div>

          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Comments / additional info</span>
            <textarea rows={3} value={data.comments || ""} onChange={(e) => set("comments", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={needVolunteers} onChange={(e) => setNeedVolunteers(e.target.checked)} data-testid={`${meta.testid_prefix}-need-volunteers`} />
            <span>We need volunteers</span>
          </label>
        </div>
      )}

      {/* ===== Housing project ===== */}
      {type === "housing_project" && (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Project title *</span>
            <input required value={data.title || ""} onChange={(e) => set("title", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-title`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Description *</span>
            <textarea required rows={5} value={data.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-description`} placeholder="Who needs housing? What kind of project? Family size, urgency, current situation." />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Housing type *</span>
            <div className="relative mt-1">
              <select required value={data.housing_type || ""} onChange={(e) => set("housing_type", e.target.value)} className="w-full appearance-none rounded-lg border px-3 py-2 text-sm bg-transparent pr-8" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-housing-type`}>
                <option value="" disabled>Select a type</option>
                {cfg?.housing_types?.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
            </div>
          </label>
          {data.housing_type === "Other" && (
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-wider">Specify type *</span>
              <input required value={categoryOther} onChange={(e) => setCategoryOther(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-housing-type-other`} />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Family / beneficiary name</span>
            <input value={data.family_name || ""} onChange={(e) => set("family_name", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Estimated cost (USD)</span>
            <input type="number" min="0" value={data.estimated_cost || ""} onChange={(e) => set("estimated_cost", Number(e.target.value) || null)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">City *</span>
            <input required value={data.city || ""} onChange={(e) => set("city", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Country *</span>
            <input required value={data.country || ""} onChange={(e) => set("country", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Website / more info link</span>
            <input type="url" value={data.website || ""} onChange={(e) => set("website", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="https://" />
          </label>

          {/* Photo upload */}
          <div className="md:col-span-2">
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Photo (optional, &lt; 3MB)</div>
            <div className="flex items-start gap-4">
              <label className="wofy-btn-ghost cursor-pointer" data-testid={`${meta.testid_prefix}-image-btn`}>
                <Upload className="w-4 h-4" /> Upload image
                <input type="file" accept="image/*" className="hidden" onChange={onImagePicked} />
              </label>
              {data.image_url && (
                <div className="relative">
                  <img src={data.image_url} alt="preview" className="h-24 w-40 object-cover rounded-xl" />
                  <button type="button" onClick={() => set("image_url", "")} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow" aria-label="Remove image">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Help offer ===== */}
      {type === "help_offer" && (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Headline / what you'd like to offer *</span>
            <input required value={data.title || ""} onChange={(e) => set("title", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-title`} placeholder="e.g. Free legal advice for tenants (2 hrs/wk)" />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Details *</span>
            <textarea required rows={5} value={data.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-description`} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Type of help *</span>
            <div className="relative mt-1">
              <select required value={data.offer_type || ""} onChange={(e) => set("offer_type", e.target.value)} className="w-full appearance-none rounded-lg border px-3 py-2 text-sm bg-transparent pr-8" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-offer-type`}>
                <option value="" disabled>Select</option>
                {cfg?.help_offer_types?.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
            </div>
          </label>
          {data.offer_type === "Other" && (
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-wider">Specify *</span>
              <input required value={categoryOther} onChange={(e) => setCategoryOther(e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Availability</span>
            <input value={data.availability || ""} onChange={(e) => set("availability", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="e.g. Weekends, evenings, on-call" />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">City *</span>
            <input required value={data.city || ""} onChange={(e) => set("city", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Country *</span>
            <input required value={data.country || ""} onChange={(e) => set("country", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!data.remote_ok} onChange={(e) => set("remote_ok", e.target.checked)} />
            <span>I can help remotely / online</span>
          </label>
        </div>
      )}

      {/* ===== Change Maker ===== */}
      {type === "change_maker" && (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Change maker full name *</span>
            <input required value={data.full_name || ""} onChange={(e) => set("full_name", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-full-name`} placeholder="e.g. Wangari Maathai" />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Short description * <span className="normal-case opacity-60">(1-2 lines)</span></span>
            <input required maxLength={200} value={data.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-short-desc`} placeholder="What are they known for? (one-liner)" />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Long description *</span>
            <textarea required rows={7} value={data.long_description || ""} onChange={(e) => set("long_description", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-long-desc`} placeholder="Their story, work, and impact." />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Website link</span>
            <input type="url" value={data.website || ""} onChange={(e) => set("website", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="https://" data-testid={`${meta.testid_prefix}-website`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Social media links <span className="normal-case opacity-60">(comma-separated URLs)</span></span>
            <input value={data.social_links || ""} onChange={(e) => set("social_links", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="https://twitter.com/…, https://linkedin.com/…" data-testid={`${meta.testid_prefix}-social`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Video links <span className="normal-case opacity-60">(YouTube/Vimeo, comma-separated)</span></span>
            <input value={data.video_links || ""} onChange={(e) => set("video_links", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} placeholder="https://youtube.com/watch?v=…" data-testid={`${meta.testid_prefix}-videos`} />
          </label>
          <label className="md:col-span-2 block">
            <span className="text-[11px] font-mono uppercase tracking-wider">Comments</span>
            <textarea rows={3} value={data.comments || ""} onChange={(e) => set("comments", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid={`${meta.testid_prefix}-comments`} placeholder="Anything else moderators should know" />
          </label>
          {/* Photo upload */}
          <div className="md:col-span-2">
            <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Photo (optional, &lt; 3MB)</div>
            <div className="flex items-start gap-4">
              <label className="wofy-btn-ghost cursor-pointer" data-testid={`${meta.testid_prefix}-image-btn`}>
                <Upload className="w-4 h-4" /> Upload image
                <input type="file" accept="image/*" className="hidden" onChange={onImagePicked} />
              </label>
              {data.image_url && (
                <div className="relative">
                  <img src={data.image_url} alt="preview" className="h-24 w-40 object-cover rounded-xl" />
                  <button type="button" onClick={() => set("image_url", "")} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow" aria-label="Remove image">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="wofy-divider" />

      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid={`${meta.testid_prefix}-consent`} />
        <span>
          I confirm the information is accurate to the best of my knowledge and I agree it will be reviewed by Social Pulse moderators before being published.
        </span>
      </label>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {RECAPTCHA_SITE_KEY && (
          <p className="text-[10px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
            Protected by reCAPTCHA · <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="wofy-btn-primary"
          data-testid={`${meta.testid_prefix}-submit`}
        >
          {busy ? "Submitting…" : "Submit for review"}
        </button>
      </div>
    </form>
  );
}
